import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CourseQuizQuestion from "../../CourseQuiz/CourseQuizQuestion";
import Modal from "../../Modal/Modal";
import { useFormikContext } from "formik";
import { questionTypes } from "../../../constants";
import { without, sortBy } from "lodash";

/**
 * We won't be passing in the correct match to the question presented to the learner
 * so we need to replace it with a blank selectedMatch
 */
const getMatchChoicesForPreview = matchChoices => {
  return matchChoices
    ? matchChoices.map(({ correctMatch, ...matchChoiceProps }) => ({
        ...matchChoiceProps,
        selectedMatch: ""
      }))
    : null;
};

const QuestionPreview = ({ onClose, open, isSurveyQuestion }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { values } = useFormikContext();
  const [currentAnswer, setCurrentAnswer] = useState([]);
  const [answers, setAnswers] = useState([]);

  const textQuestionTypes = [
    questionTypes.longAnswer,
    questionTypes.shortAnswer
  ];

  useEffect(() => {
    if (
      values.questionType === questionTypes.ordering ||
      values.questionType === questionTypes.multiSelect ||
      values.questionType === questionTypes.multipleChoice ||
      values.questionType === questionTypes.trueFalse
    ) {
      setCurrentAnswer(
        values.answers ? values.answers.map(({ id }) => ({ id })) : []
      );
      setAnswers(sortBy(values.answers, ["order"]));
    } else if (
      textQuestionTypes.indexOf(values.questionType) < 0 &&
      values.answers &&
      values.answers.length > 0
    ) {
      setAnswers(
        values.answers.map((answer, index) => ({
          ...answer,
          id: answer.id ? answer.id : `${index}`,
          correct: answer.correct ? answer.correct : true
        }))
      );
      const correctAnswer = answers.find(answer => answer.correct === true);
      if (correctAnswer) {
        setCurrentAnswer(correctAnswer.id);
      }
    } else {
      setCurrentAnswer("");
    }
  }, [values]);

  const handleAnswerChange = newAnswer => {
    if (
      values.questionType === questionTypes.matching ||
      values.questionType === questionTypes.fillBlanks
    ) {
      return;
    } else if (values.questionType === questionTypes.longAnswer) {
      // TODO: fix snake case
      setCurrentAnswer(newAnswer.answer_text);
    } else if (values.questionType === questionTypes.multiSelect) {
      let newAnswers = [...currentAnswer];
      if (newAnswers.indexOf(newAnswer.id) !== -1) {
        newAnswers = without(currentAnswer, newAnswer.id);
      } else {
        newAnswers.push(newAnswer.id);
      }
      setCurrentAnswer(newAnswers);
    } else {
      setCurrentAnswer([newAnswer.id]);
    }
  };

  return (
    <Modal
      scrollable
      disableEnforceFocus
      showCloseIcon
      maxWidth="lg"
      fillHeight
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("editQuestion.previewQuestionModalTitle")}
        </Typography>
      }
      dialogContent={
        <Box display="flex" flexDirection="column" flex={1} height="100%">
          <Box
            display="flex"
            flexDirection="column"
            bgcolor={theme.palette.inputBackground}
            justifyContent="flex-start"
            p={4}
            minWidth={{ xs: "100%", md: 600 }}
            flex={1}
          >
            <CourseQuizQuestion
              text={values.text}
              answerChoices={answers}
              points={values.points}
              currentAnswer={currentAnswer}
              onAnswerChange={(type, answer) => handleAnswerChange(answer)}
              questionType={values.questionType}
              fillInTheBlanks={values.fillInTheBlanks}
              allowHtml={
                values.additionalOptions && values.additionalOptions.allowHtml
              }
              isSurvey={isSurveyQuestion}
              matchChoices={getMatchChoicesForPreview(values.matchChoices)}
              matchClues={values.matchClues}
            />
          </Box>
        </Box>
      }
      open={open}
      onClose={onClose}
    />
  );
};
QuestionPreview.propTypes = {
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  isSurveyQuestion: PropTypes.bool
};

export default QuestionPreview;
